import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { apiUrl, apiFetchJson } from './api';
import { orderRefLabel } from './orderRef';
import { formatMicroUsdt } from './usdtFormat';
import { CHAT_CONTACT_BLOCKED_MESSAGE, hasBlockedContact } from './chatContactPolicy';

export interface OrderChatProps {
  orderId: number;
  token: string;
  orderStatus: string;
  myRole: 'customer' | 'executor';
  /** Открыть чат сразу (из inbox или повторно из бота) */
  autoOpen?: boolean;
  /** Меняется при каждом входе из кнопки бота — чтобы чат открылся снова */
  openSignal?: number;
  /** Собеседник для серверной пометки прочитанного */
  peerId?: number | null;
  /** Отклик (до выбора исполнителя) — карточка условий в чате */
  responseId?: number | null;
  /** Бюджет заказа (для подписи «согласен с бюджетом») */
  orderBudgetMicro?: number | null;
  /** Заказ по услуге — подпись кнопки «Посмотреть услугу» */
  isServiceOrder?: boolean;
  /** Закрыть чат и показать карточку заказа/услуги */
  onViewOrder?: () => void;
  /** После назначения исполнителя из чата */
  onAssigned?: () => void;
}

type ChatMsg = {
  id: number;
  body: string;
  created_at: string;
  is_mine: boolean;
  is_system?: boolean;
  sender_role: string;
  attachment_url?: string | null;
  attachment_mime?: string | null;
  attachment_name?: string | null;
};

const TOGGLE_BTN: CSSProperties = {
  display: 'flex',
  width: '100%',
  maxWidth: '420px',
  boxSizing: 'border-box',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  marginTop: 16,
  padding: '12px 14px',
  background: 'rgba(var(--fc-accent-rgb), 0.12)',
  border: '2px solid rgba(var(--fc-accent-rgb), 0.35)',
  borderRadius: 10,
  color: 'var(--fc-text)',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit'
};

function readStorageKey(orderId: number): string {
  return `fcyx_order_chat_read_${orderId}`;
}

function getLastReadMessageId(orderId: number): number {
  try {
    const raw = localStorage.getItem(readStorageKey(orderId));
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function setLastReadMessageId(orderId: number, messageId: number) {
  try {
    localStorage.setItem(readStorageKey(orderId), String(messageId));
  } catch {
    /* ignore */
  }
}

function computeHasUnread(messages: ChatMsg[], orderId: number): boolean {
  const lastRead = getLastReadMessageId(orderId);
  return messages.some((m) => !m.is_mine && m.id > lastRead);
}

function maxMessageId(messages: ChatMsg[]): number {
  if (!messages.length) return 0;
  return messages.reduce((max, m) => (m.id > max ? m.id : max), 0);
}

function formatMessageTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function dayKeyFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return dayKeyFromDate(d);
}

function formatDateLabel(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const key = dayKey(iso);
  const todayKey = dayKeyFromDate(new Date());
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = dayKeyFromDate(yesterday);
  if (key === todayKey) return 'Сегодня';
  if (key === yesterdayKey) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

type ChatRenderGroup = {
  senderKey: string;
  isMine: boolean;
  senderRole: string;
  messages: ChatMsg[];
};

type ChatRenderItem =
  | { type: 'date'; key: string; label: string }
  | { type: 'group'; key: string; group: ChatRenderGroup };

function buildChatRenderItems(messages: ChatMsg[]): ChatRenderItem[] {
  const items: ChatRenderItem[] = [];
  let lastDay = '';
  let currentGroup: ChatRenderGroup | null = null;

  const flushGroup = () => {
    if (!currentGroup) return;
    items.push({
      type: 'group',
      key: `g-${currentGroup.messages[0].id}`,
      group: currentGroup
    });
    currentGroup = null;
  };

  for (const m of messages) {
    const day = dayKey(m.created_at);
    if (day !== lastDay) {
      flushGroup();
      items.push({ type: 'date', key: `d-${day}-${m.id}`, label: formatDateLabel(m.created_at) });
      lastDay = day;
    }
    if (m.is_system || m.sender_role === 'system') {
      flushGroup();
      items.push({
        type: 'group',
        key: `sys-${m.id}`,
        group: {
          senderKey: `system-${m.id}`,
          isMine: false,
          senderRole: 'system',
          messages: [m]
        }
      });
      continue;
    }
    const senderKey = m.is_mine ? 'mine' : m.sender_role;
    if (!currentGroup || currentGroup.senderKey !== senderKey) {
      flushGroup();
      currentGroup = {
        senderKey,
        isMine: m.is_mine,
        senderRole: m.sender_role,
        messages: [m]
      };
    } else {
      currentGroup.messages.push(m);
    }
  }
  flushGroup();
  return items;
}

function ChatAttachment({
  url,
  mime,
  name,
  token
}: {
  url: string;
  mime?: string | null;
  name?: string | null;
  token: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isImage = Boolean(mime && mime.startsWith('image/') && !/svg/i.test(mime));

  useEffect(() => {
    let objectUrl = '';
    let cancel = false;
    const abs = url.startsWith('http') ? url : apiUrl(url);
    fetch(abs, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error('file');
        return r.blob();
      })
      .then((blob) => {
        if (cancel) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancel) setFailed(true);
      });
    return () => {
      cancel = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, token]);

  if (failed) {
    return <div className="fc-order-chat-msg__file">Файл недоступен</div>;
  }
  if (!src) {
    return <div className="fc-order-chat-msg__file">Загрузка файла…</div>;
  }
  if (isImage) {
    return <img src={src} alt="" className="fc-order-chat-msg__image" />;
  }
  return (
    <div className="fc-order-chat-msg__file">
      <a href={src} download={name || 'file'}>
        📎 {name || 'Скачать файл'}
      </a>
    </div>
  );
}

function renderMessageBubble(m: ChatMsg, token: string) {
  const time = formatMessageTime(m.created_at);
  const isSystem = Boolean(m.is_system || m.sender_role === 'system');
  return (
    <div
      className={`fc-order-chat-msg__bubble${isSystem ? ' fc-order-chat-msg__bubble--system' : ''}`}
      style={
        isSystem
          ? {
              background: 'rgba(255, 193, 7, 0.14)',
              border: '1px solid rgba(255, 193, 7, 0.45)',
              color: 'var(--fc-text)',
              width: '100%',
              maxWidth: '100%'
            }
          : undefined
      }
    >
      {m.attachment_url ? (
        <ChatAttachment
          url={m.attachment_url}
          mime={m.attachment_mime}
          name={m.attachment_name}
          token={token}
        />
      ) : null}
      <div className="fc-order-chat-msg__bubble-row">
        {m.body ? <div className="fc-order-chat-msg__text">{m.body}</div> : null}
        {time ? <span className="fc-order-chat-msg__time-in">{time}</span> : null}
      </div>
    </div>
  );
}

function ChatStatusDot({ unread }: { unread: boolean }) {
  return (
    <span
      className={`fc-chat-status-dot${unread ? ' fc-chat-status-dot--unread' : ''}`}
      aria-hidden
      title={unread ? 'Есть новые сообщения' : 'Нет новых сообщений'}
    />
  );
}

type ResponseOffer = {
  id: number;
  status: string;
  message: string;
  proposed_budget: number | null;
  eta_value: number | null;
  eta_unit: string | null;
  first_name?: string | null;
  last_name?: string | null;
  executor_id?: number;
};

function formatEta(value: number | null, unit: string | null): string {
  if (value == null || !unit) return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  const abs = Math.abs(n) % 100;
  const d = abs % 10;
  const forms: [string, string, string] =
    unit === 'hours'
      ? ['час', 'часа', 'часов']
      : unit === 'weeks'
        ? ['неделя', 'недели', 'недель']
        : unit === 'months'
          ? ['месяц', 'месяца', 'месяцев']
          : ['день', 'дня', 'дней'];
  let word = forms[2];
  if (abs < 10 || abs > 20) {
    if (d === 1) word = forms[0];
    else if (d >= 2 && d <= 4) word = forms[1];
  }
  return `${n} ${word}`;
}

function tgConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const tg = (window as unknown as { Telegram?: { WebApp?: { showConfirm?: (m: string, cb: (ok: boolean) => void) => void } } })
        .Telegram?.WebApp;
      if (tg && typeof tg.showConfirm === 'function') {
        tg.showConfirm(message, (ok: boolean) => resolve(!!ok));
        return;
      }
    } catch {
      /* ignore */
    }
    resolve(window.confirm(message));
  });
}

export default function OrderChat({
  orderId,
  token,
  orderStatus,
  myRole,
  autoOpen = false,
  openSignal = 0,
  peerId = null,
  responseId = null,
  orderBudgetMicro = null,
  isServiceOrder = false,
  onViewOrder,
  onAssigned
}: OrderChatProps) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [canPost, setCanPost] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [chatOpen, setChatOpen] = useState(Boolean(autoOpen));
  const [hasUnread, setHasUnread] = useState(false);
  const [offer, setOffer] = useState<ResponseOffer | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatOpenRef = useRef(false);
  const sendingRef = useRef(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    chatOpenRef.current = chatOpen;
  }, [chatOpen]);

  useEffect(() => {
    if (autoOpen || openSignal > 0) setChatOpen(true);
  }, [autoOpen, openSignal, orderId]);

  useEffect(() => {
    const onOpen = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      if (Number(detail.orderId) === Number(orderId)) setChatOpen(true);
    };
    window.addEventListener('fcyx-open-order-chat', onOpen as EventListener);
    return () => window.removeEventListener('fcyx-open-order-chat', onOpen as EventListener);
  }, [orderId]);

  const closeChat = useCallback(() => {
    setChatOpen(false);
  }, []);

  const markRead = useCallback(
    (msgs: ChatMsg[]) => {
      const maxId = maxMessageId(msgs);
      if (maxId > 0) {
        setLastReadMessageId(orderId, maxId);
      }
      setHasUnread(false);
      if (token) {
        void fetch(apiUrl(`/api/messages/inbox/${orderId}/read`), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(peerId != null ? { peer_id: peerId } : {})
        }).catch(() => undefined);
      }
    },
    [orderId, token, peerId]
  );

  const load = useCallback(async () => {
    if (!token || !orderId) return;
    setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const q =
        peerId != null && Number.isFinite(Number(peerId))
          ? `?peer_id=${encodeURIComponent(String(peerId))}`
          : '';
      const { response: r, data } = await apiFetchJson(`/api/orders/${oid}/messages${q}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) {
        setError(String(data.error || 'Не удалось загрузить чат'));
        setMessages([]);
        setCanPost(false);
        return;
      }
      if (data.success) {
        const next = (data.messages as ChatMsg[]) || [];
        setMessages(next);
        setCanPost(Boolean(data.can_post));
        if (data.pending_response) {
          setOffer(data.pending_response as ResponseOffer);
        } else if (responseId == null) {
          setOffer(null);
        }
        if (!chatOpenRef.current) {
          setHasUnread(computeHasUnread(next, orderId));
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Ошибка сети';
      setMessages((prev) => {
        if (prev.length === 0) setError(msg);
        return prev;
      });
    } finally {
      setLoading(false);
    }
  }, [orderId, token, peerId, responseId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const loadOffer = useCallback(async () => {
    if (!token || !responseId) return;
    try {
      const { response: r, data } = await apiFetchJson(`/api/responses/${responseId}/offer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok || !data.success || !data.response) {
        setOffer(null);
        return;
      }
      setOffer(data.response as ResponseOffer);
    } catch {
      setOffer(null);
    }
  }, [token, responseId]);

  useEffect(() => {
    void loadOffer();
  }, [loadOffer]);

  useEffect(() => {
    if (!canPost || orderStatus === 'finished') return;
    const t = window.setInterval(() => void load(), 12000);
    return () => window.clearInterval(t);
  }, [load, canPost, orderStatus]);

  useEffect(() => {
    if (!chatOpen) return;
    markRead(messages);
  }, [chatOpen, messages, markRead]);

  useEffect(() => {
    if (!chatOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatOpen, messages]);

  useEffect(() => {
    if (!chatOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [chatOpen]);

  useEffect(() => {
    const win = window as unknown as { __fcyxOrderChatOpen?: boolean };
    if (chatOpen) {
      win.__fcyxOrderChatOpen = true;
    } else {
      delete win.__fcyxOrderChatOpen;
    }
    window.dispatchEvent(new CustomEvent('fcyx-order-chat-state', { detail: { open: chatOpen } }));
    return () => {
      delete win.__fcyxOrderChatOpen;
      window.dispatchEvent(new CustomEvent('fcyx-order-chat-state', { detail: { open: false } }));
    };
  }, [chatOpen]);

  useEffect(() => {
    const onCloseRequest = () => closeChat();
    window.addEventListener('fcyx-close-order-chat', onCloseRequest);
    return () => window.removeEventListener('fcyx-close-order-chat', onCloseRequest);
  }, [closeChat]);

  useEffect(() => {
    // @ts-ignore
    const tg = window?.Telegram?.WebApp;
    if (!tg?.BackButton || !chatOpen) {
      try {
        tg?.BackButton?.hide?.();
      } catch {
        /* ignore */
      }
      return;
    }
    const onBack = () => closeChat();
    try {
      tg.BackButton.show();
      tg.BackButton.onClick(onBack);
    } catch {
      /* ignore */
    }
    return () => {
      try {
        tg.BackButton.offClick(onBack);
        tg.BackButton.hide();
      } catch {
        /* ignore */
      }
    };
  }, [chatOpen, closeChat]);

  const notifyContactBlocked = () => {
    setError(CHAT_CONTACT_BLOCKED_MESSAGE);
    try {
      const tg = (window as unknown as { Telegram?: { WebApp?: { showAlert?: (m: string) => void } } })
        .Telegram?.WebApp;
      tg?.showAlert?.(CHAT_CONTACT_BLOCKED_MESSAGE);
    } catch {
      /* ignore */
    }
  };

  const canAssign =
    myRole === 'customer' &&
    orderStatus === 'open' &&
    offer != null &&
    String(offer.status) === 'pending' &&
    !assignBusy;

  const assignExecutor = async () => {
    if (!canAssign || !offer) return;
    const name = [offer.first_name, offer.last_name].filter(Boolean).join(' ').trim() || 'этого исполнителя';
    const ok = await tgConfirm(`Вы точно хотите выбрать исполнителем: ${name}?`);
    if (!ok) return;
    setAssignBusy(true);
    setError(null);
    try {
      const r = await fetch(apiUrl(`/api/responses/${offer.id}/accept`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deadline_value: offer.eta_value,
          deadline_unit: offer.eta_unit
        })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.success) {
        setError(String(data.error || 'Не удалось выбрать исполнителя'));
        try {
          const tg = (window as unknown as { Telegram?: { WebApp?: { showAlert?: (m: string) => void } } })
            .Telegram?.WebApp;
          tg?.showAlert?.(String(data.error || 'Не удалось выбрать исполнителя'));
        } catch {
          /* ignore */
        }
        return;
      }
      await Promise.all([load(), loadOffer()]);
      onAssigned?.();
    } catch {
      setError('Ошибка сети');
    } finally {
      setAssignBusy(false);
    }
  };

  const send = async () => {
    const b = text.trim();
    if (!b || !canPost || sendingRef.current) return;
    if (hasBlockedContact(b)) {
      notifyContactBlocked();
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setText('');
    setError(null);
    try {
      const r = await fetch(apiUrl(`/api/orders/${orderId}/messages`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          body: b,
          ...(peerId != null ? { peer_id: peerId } : {})
        })
      });
      const data = await r.json();
      if (!r.ok) {
        setText(b);
        const err = data.error || 'Не отправлено';
        setError(err);
        if (String(err).includes('По правилам платформы')) {
          try {
            const tg = (window as unknown as { Telegram?: { WebApp?: { showAlert?: (m: string) => void } } })
              .Telegram?.WebApp;
            tg?.showAlert?.(err);
          } catch {
            /* ignore */
          }
        }
        return;
      }
      if (data.message) {
        setMessages((prev) => {
          const incoming = data.message as ChatMsg;
          if (prev.some((m) => m.id === incoming.id)) return prev;
          const next = [...prev, incoming];
          if (chatOpen) markRead(next);
          return next;
        });
      } else {
        void load();
      }
    } catch {
      setText(b);
      setError('Ошибка сети');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canPost) return;
    if (hasBlockedContact(file.name)) {
      notifyContactBlocked();
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (peerId != null) fd.append('peer_id', String(peerId));
      const r = await fetch(apiUrl(`/api/orders/${orderId}/messages/upload`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || 'Не удалось отправить файл');
        return;
      }
      if (data.message) {
        setMessages((prev) => {
          const next = [...prev, data.message as ChatMsg];
          if (chatOpen) markRead(next);
          return next;
        });
      } else {
        void load();
      }
    } catch {
      setError('Ошибка сети');
    } finally {
      setUploading(false);
    }
  };

  const labelFor = (sender_role: string, is_mine: boolean) => {
    if (sender_role === 'system') return 'FreelanCYX';
    if (is_mine) return 'Вы';
    if (sender_role === 'customer') return 'Заказчик';
    return 'Исполнитель';
  };

  const orderLabel = orderRefLabel(orderId);
  const chatRenderItems = buildChatRenderItems(messages);

  const chatBody = (
    <>
      <p className="fc-order-chat-screen__hint">
        Общайтесь только здесь — чтобы сделки не уходили в обход биржи. Можно прикрепить файл (картинка,
        аудио, PDF и т.д.).
      </p>

      {offer && String(offer.status) === 'pending' ? (
        <div className="fc-order-chat-offer">
          <div className="fc-order-chat-offer__title">Отклик исполнителя</div>
          {offer.message ? (
            <p className="fc-order-chat-offer__msg">{offer.message}</p>
          ) : (
            <p className="fc-order-chat-offer__msg fc-order-chat-offer__msg--muted">Без текста отклика</p>
          )}
          <div className="fc-order-chat-offer__meta">
            {offer.proposed_budget != null &&
            Number.isFinite(Number(offer.proposed_budget)) &&
            Number(offer.proposed_budget) > 0 ? (
              <span>
                💰 Предлагает свою сумму:{' '}
                <b>{formatMicroUsdt(Number(offer.proposed_budget))} USDT</b>
                {orderBudgetMicro != null && Number.isFinite(Number(orderBudgetMicro)) ? (
                  <span className="fc-order-chat-offer__dim">
                    {' '}
                    (в заказе {formatMicroUsdt(Number(orderBudgetMicro))} USDT)
                  </span>
                ) : null}
              </span>
            ) : (
              <span>
                💰 Согласен с бюджетом заказа
                {orderBudgetMicro != null && Number.isFinite(Number(orderBudgetMicro)) ? (
                  <>
                    : <b>{formatMicroUsdt(Number(orderBudgetMicro))} USDT</b>
                  </>
                ) : null}
              </span>
            )}
          </div>
          {formatEta(offer.eta_value, offer.eta_unit) ? (
            <div className="fc-order-chat-offer__meta">
              ⏱ Готов выполнить за <b>{formatEta(offer.eta_value, offer.eta_unit)}</b>
            </div>
          ) : null}
        </div>
      ) : null}

      {orderStatus === 'open' && canPost && offer && String(offer.status) === 'pending' ? (
        <p className="fc-order-chat-screen__muted">
          Можно уточнить детали в переписке. Назначение исполнителя — кнопкой сверху (с подтверждением).
        </p>
      ) : null}

      {orderStatus === 'open' && canPost && !(offer && String(offer.status) === 'pending') && (
        <p className="fc-order-chat-screen__muted" style={{ color: 'var(--fc-accent-strong)' }}>
          Ожидание оплаты эскроу. Можно обсудить детали; работа стартует после оплаты заказчиком.
        </p>
      )}

      {orderStatus === 'open' && !canPost && !(offer && String(offer.status) === 'pending') && (
        <p className="fc-order-chat-screen__muted">
          {myRole === 'customer'
            ? 'Откройте диалог с исполнителем из «Сообщения», чтобы уточнить детали или назначить его.'
            : 'Чат доступен после отклика — заказчик может писать вам до назначения.'}
        </p>
      )}

      {error ? <p className="fc-order-chat-screen__error">{error}</p> : null}

      <div className="fc-order-chat-screen__messages">
        {messages.length === 0 && canPost ? (
          <p className="fc-order-chat-screen__empty">Пока нет сообщений — напишите первым.</p>
        ) : null}
        {messages.length === 0 && !canPost ? (
          <p className="fc-order-chat-screen__empty">Пока нет сообщений.</p>
        ) : null}
        {chatRenderItems.map((item) => {
          if (item.type === 'date') {
            return (
              <div key={item.key} className="fc-order-chat-date">
                {item.label}
              </div>
            );
          }
          const { group } = item;
          const isSystem = group.senderRole === 'system';
          const author = labelFor(group.senderRole, group.isMine);
          const count = group.messages.length;
          return (
            <div
              key={item.key}
              className={`fc-order-chat-group${
                isSystem
                  ? ' fc-order-chat-group--system'
                  : group.isMine
                    ? ' fc-order-chat-group--mine'
                    : ' fc-order-chat-group--theirs'
              }`}
              style={isSystem ? { alignItems: 'stretch', width: '100%' } : undefined}
            >
              <div className="fc-order-chat-group__author">{author}</div>
              <div className="fc-order-chat-group__stack">
                {group.messages.map((m, idx) => {
                  const pos =
                    count === 1 ? 'single' : idx === 0 ? 'first' : idx === count - 1 ? 'last' : 'mid';
                  return (
                    <div
                      key={m.id}
                      className={`fc-order-chat-group__item fc-order-chat-group__item--${pos}`}
                    >
                      {renderMessageBubble(m, token)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {canPost ? (
        <div className="fc-order-chat-screen__composer">
          <p className="fc-order-chat-screen__warn">
            Не передавайте телефон, почту и контакты вне чата — это правило безопасности биржи.
          </p>
          <div className="fc-order-chat-screen__composer-box">
            <textarea
              placeholder={myRole === 'customer' ? 'Сообщение исполнителю…' : 'Сообщение заказчику…'}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              className="fc-order-chat-screen__input"
            />
            <div className="fc-order-chat-screen__composer-actions">
              <label className="fc-order-chat-screen__attach">
                <input
                  type="file"
                  accept="image/*,audio/*,video/*,.pdf,.zip"
                  className="fc-order-chat-screen__attach-input"
                  disabled={uploading}
                  onChange={(e) => void onPickFile(e)}
                />
                {uploading ? '⏳' : '📎'}
              </label>
              <button
                type="button"
                className="fc-order-chat-screen__send"
                onClick={() => void send()}
                disabled={uploading || sending || !text.trim()}
              >
                {sending ? 'Отправка…' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!canPost && orderStatus === 'finished' && messages.length > 0 && (
        <p className="fc-order-chat-screen__muted fc-order-chat-screen__muted--bottom">
          Заказ завершён — переписка только для просмотра.
        </p>
      )}
    </>
  );

  const chatModal =
    chatOpen && typeof document !== 'undefined'
      ? createPortal(
          <div role="dialog" aria-modal="true" aria-label="Чат по заказу" className="fc-order-chat-screen">
            <div className="fc-order-chat-screen__topbar">
              <button type="button" className="fc-back" aria-label="Назад" onClick={closeChat}>
                ←
              </button>
              <div className="fc-order-chat-screen__title-wrap">
                <h2 className="fc-order-chat-screen__title">
                  <span className="fc-order-chat-screen__title-row">
                    <span aria-hidden>💬</span>
                    <span>Чат по заказу</span>
                    <ChatStatusDot unread={false} />
                  </span>
                </h2>
                <div className="fc-order-chat-screen__subtitle-row">
                  <p className="fc-order-chat-screen__subtitle">{orderLabel}</p>
                  {onViewOrder ? (
                    <button
                      type="button"
                      className="fc-order-chat-screen__view-order"
                      onClick={onViewOrder}
                    >
                      {isServiceOrder ? 'Посмотреть услугу' : 'Посмотреть заказ'}
                    </button>
                  ) : null}
                </div>
                {canAssign ? (
                  <button
                    type="button"
                    className="fc-order-chat-screen__assign"
                    disabled={assignBusy}
                    onClick={() => void assignExecutor()}
                  >
                    {assignBusy ? 'Назначаем…' : 'Назначить исполнителем'}
                  </button>
                ) : null}
              </div>
              <button type="button" className="fc-btn fc-btn--dark fc-order-chat-screen__close" onClick={closeChat}>
                ✕
              </button>
            </div>
            <div className="fc-order-chat-screen__body">{chatBody}</div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        type="button"
        id="freelancyx-order-chat-toggle"
        style={TOGGLE_BTN}
        onClick={() => setChatOpen(true)}
        disabled={loading}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span aria-hidden>💬</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span>Чат по заказу</span>
            <ChatStatusDot unread={hasUnread} />
          </span>
        </span>
        <span style={{ color: 'var(--fc-accent)', fontSize: 14, flexShrink: 0 }}>{loading ? '…' : 'Открыть'}</span>
      </button>

      {chatModal}
    </>
  );
}
